// THIS IS AN AUTOGENERATED FILE. DO NOT EDIT THIS FILE DIRECTLY.

import { useMutation, useMutationState } from "@tanstack/react-query";
import { useHttpClient } from "@toggl/sync/http";
import {
  LivetestLiveTestAnswer,
  LivetestPostCopyPasteEvents,
  LivetestStartTestBody,
  LivetestTestFeedback,
} from "../types";
import { MutationOptions } from "../utils/mutations";
import {
  finishTestMutationKey,
  patchTestMutationKey,
  postTestCopyPasteEventsMutationKey,
  postTestFeedbackMutationKey,
  startTestMutationKey,
  tryTestMutationKey,
  uploadNotificationMutationKey,
} from "./keys";
import {
  finishTest,
  patchTest,
  postTestCopyPasteEvents,
  postTestFeedback,
  startTest,
  tryTest,
  uploadNotification,
} from "./requests";

export type UploadNotificationMutationVariables = { upload_id: number };

export type UploadNotificationMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof uploadNotification,
    UploadNotificationMutationVariables,
    TContext
  >;

/**
 * Handle a webhook notification that an upload has been completed
 */
export function useUploadNotificationMutation<TContext = unknown>(
  options: UploadNotificationMutationOptions<TContext> = {},
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ upload_id }) => uploadNotification(httpClient, upload_id),
    mutationKey: uploadNotificationMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending uploadNotification mutation. */
export function useUploadNotificationMutationVariables() {
  return useMutationState({
    filters: { mutationKey: uploadNotificationMutationKey, status: "pending" },
    select: (mutation) =>
      mutation.state.variables as UploadNotificationMutationVariables,
  });
}

export type StartTestMutationVariables = {
  job_opening_slug: string;
  slug: string;
  data: LivetestStartTestBody;
};

export type StartTestMutationOptions<TContext = unknown> = MutationOptions<
  typeof startTest,
  StartTestMutationVariables,
  TContext
>;

/**
 * Starts a live test from a test slug.
Presigned upload URLs for video questions are generated by the start-test endpoint. You can upload to the same URL multiple times.
 */
export function useStartTestMutation<TContext = unknown>(
  options: StartTestMutationOptions<TContext> = {},
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ job_opening_slug, slug, data }) =>
      startTest(httpClient, job_opening_slug, slug, data),
    mutationKey: startTestMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending startTest mutation. */
export function useStartTestMutationVariables() {
  return useMutationState({
    filters: { mutationKey: startTestMutationKey, status: "pending" },
    select: (mutation) =>
      mutation.state.variables as StartTestMutationVariables,
  });
}

export type PatchTestMutationVariables = {
  job_opening_slug: string;
  slug: string;
  data: Partial<LivetestLiveTestAnswer>;
};

export type PatchTestMutationOptions<TContext = unknown> = MutationOptions<
  typeof patchTest,
  PatchTestMutationVariables,
  TContext
>;

/**
 * Patches a test, altering the state or a question selected options by the provided ID.
When re-uploading a video question use the same URL, with a unique filename for each version. When the video recordings are received we will match up the correct version based on the filename.
 */
export function usePatchTestMutation<TContext = unknown>(
  options: PatchTestMutationOptions<TContext> = {},
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ job_opening_slug, slug, data }) =>
      patchTest(httpClient, job_opening_slug, slug, data),
    mutationKey: patchTestMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending patchTest mutation. */
export function usePatchTestMutationVariables() {
  return useMutationState({
    filters: { mutationKey: patchTestMutationKey, status: "pending" },
    select: (mutation) =>
      mutation.state.variables as PatchTestMutationVariables,
  });
}

export type PostTestCopyPasteEventsMutationVariables = {
  job_opening_slug: string;
  slug: string;
  data: LivetestPostCopyPasteEvents;
};

export type PostTestCopyPasteEventsMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof postTestCopyPasteEvents,
    PostTestCopyPasteEventsMutationVariables,
    TContext
  >;

/**
 * Append a new copy paste event to a live test
 */
export function usePostTestCopyPasteEventsMutation<TContext = unknown>(
  options: PostTestCopyPasteEventsMutationOptions<TContext> = {},
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ job_opening_slug, slug, data }) =>
      postTestCopyPasteEvents(httpClient, job_opening_slug, slug, data),
    mutationKey: postTestCopyPasteEventsMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending postTestCopyPasteEvents mutation. */
export function usePostTestCopyPasteEventsMutationVariables() {
  return useMutationState({
    filters: {
      mutationKey: postTestCopyPasteEventsMutationKey,
      status: "pending",
    },
    select: (mutation) =>
      mutation.state.variables as PostTestCopyPasteEventsMutationVariables,
  });
}

export type PostTestFeedbackMutationVariables = {
  job_opening_slug: string;
  slug: string;
  data: LivetestTestFeedback;
};

export type PostTestFeedbackMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof postTestFeedback,
    PostTestFeedbackMutationVariables,
    TContext
  >;

/**
 * Records feedback about the test, by test slug.
 */
export function usePostTestFeedbackMutation<TContext = unknown>(
  options: PostTestFeedbackMutationOptions<TContext> = {},
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ job_opening_slug, slug, data }) =>
      postTestFeedback(httpClient, job_opening_slug, slug, data),
    mutationKey: postTestFeedbackMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending postTestFeedback mutation. */
export function usePostTestFeedbackMutationVariables() {
  return useMutationState({
    filters: { mutationKey: postTestFeedbackMutationKey, status: "pending" },
    select: (mutation) =>
      mutation.state.variables as PostTestFeedbackMutationVariables,
  });
}

export type FinishTestMutationVariables = {
  job_opening_slug: string;
  slug: string;
};

export type FinishTestMutationOptions<TContext = unknown> = MutationOptions<
  typeof finishTest,
  FinishTestMutationVariables,
  TContext
>;

/**
 * Finishes a test, returning the results and triggering the processing.
 */
export function useFinishTestMutation<TContext = unknown>(
  options: FinishTestMutationOptions<TContext> = {},
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ job_opening_slug, slug }) =>
      finishTest(httpClient, job_opening_slug, slug),
    mutationKey: finishTestMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending finishTest mutation. */
export function useFinishTestMutationVariables() {
  return useMutationState({
    filters: { mutationKey: finishTestMutationKey, status: "pending" },
    select: (mutation) =>
      mutation.state.variables as FinishTestMutationVariables,
  });
}

export type TryTestMutationVariables = {
  job_opening_slug: string;
  slug: string;
};

export type TryTestMutationOptions<TContext = unknown> = MutationOptions<
  typeof tryTest,
  TryTestMutationVariables,
  TContext
>;

/**
 * Starts a test try for an admin user and sets a cookie with a test candidate.
 */
export function useTryTestMutation<TContext = unknown>(
  options: TryTestMutationOptions<TContext> = {},
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ job_opening_slug, slug }) =>
      tryTest(httpClient, job_opening_slug, slug),
    mutationKey: tryTestMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending tryTest mutation. */
export function useTryTestMutationVariables() {
  return useMutationState({
    filters: { mutationKey: tryTestMutationKey, status: "pending" },
    select: (mutation) => mutation.state.variables as TryTestMutationVariables,
  });
}
